import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import MediaGallery from '../components/MediaGallery';
import { 
  Sparkles, Loader2, Download, Wand2, Upload, Layers,
  ChevronDown, ChevronUp, Lightbulb, Image as ImageIcon,
  ZoomIn, Palette, Check, X, RefreshCw, Info, Zap,
  Camera, Mountain, User, ShoppingBag, Utensils, Building2,
  Scissors, SunMedium, Expand, Eraser
} from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inspiration examples with real thumbnails
const INSPIRATION_EXAMPLES = [
  {
    prompt: "Luxury perfume bottle on marble surface with soft golden hour lighting, product photography",
    category: "Product",
    icon: ShoppingBag,
    color: "from-amber-500 to-orange-600"
  },
  {
    prompt: "Professional headshot of confident business woman, studio lighting, neutral background",
    category: "Portrait",
    icon: User,
    color: "from-pink-500 to-rose-600"
  },
  {
    prompt: "Majestic mountain landscape at golden hour with dramatic clouds and mirror lake reflection",
    category: "Nature",
    icon: Mountain,
    color: "from-emerald-500 to-teal-600"
  },
  {
    prompt: "Artisan sourdough bread on rustic wooden board with flour dust, food photography",
    category: "Food",
    icon: Utensils,
    color: "from-orange-500 to-red-600"
  },
  {
    prompt: "Futuristic cyberpunk cityscape at night with neon lights and rain reflections",
    category: "Sci-Fi",
    icon: Building2,
    color: "from-purple-500 to-indigo-600"
  },
  {
    prompt: "Minimalist product shot of wireless earbuds on clean white background, commercial style",
    category: "Tech",
    icon: Camera,
    color: "from-blue-500 to-cyan-600"
  }
];

// Friendly generation messages
const GENERATION_MESSAGES = [
  "Crafting your vision...",
  "Adding artistic details...",
  "Perfecting the lighting...",
  "Enhancing the composition...",
  "Almost there..."
];

// Style options
const STYLE_OPTIONS = [
  { value: 'photorealistic', label: 'Photorealistic', desc: 'Ultra-realistic photography' },
  { value: 'hyperrealism', label: 'Hyperrealism', desc: 'Extremely detailed and sharp' },
  { value: 'artistic', label: 'Artistic', desc: 'Creative and stylized' },
  { value: 'animated', label: 'Animated', desc: '3D Pixar-style animation' },
  { value: 'anime', label: 'Anime', desc: 'Japanese animation style' },
  { value: 'stylized', label: 'Stylized', desc: 'Digital art illustration' }
];

const EDIT_TOOLS = [
  { id: 'bg-remove', icon: Scissors, label: 'Remove BG', desc: 'Clean background removal', prompt: 'clean white studio background' },
  { id: 'upscale', icon: ZoomIn, label: 'Upscale', desc: 'Enhance resolution & detail', prompt: 'Maximum detail and sharpness' },
  { id: 'relight', icon: SunMedium, label: 'Relight', desc: 'Change lighting', prompt: 'golden hour warm side lighting' },
  { id: 'color-grade', icon: Palette, label: 'Color Grade', desc: 'Cinematic color grading', prompt: 'Teal and orange cinematic grade' },
  { id: 'expand', icon: Expand, label: 'Expand', desc: 'Extend image borders', prompt: 'Expand the scene naturally' },
  { id: 'eraser', icon: Eraser, label: 'Clean Up', desc: 'Remove unwanted elements', prompt: 'Remove distracting elements' },
];

function EditPanel({ token, onComplete }) {
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [editPrompt, setEditPrompt] = useState('');
  const [editing, setEditing] = useState(false);
  const [editResult, setEditResult] = useState(null);
  const editFileRef = useRef(null);

  const handleEditUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditFile(file);
    setEditPreview(URL.createObjectURL(file));
    setEditResult(null);
  };

  const selectTool = (tool) => {
    setSelectedTool(tool);
    setEditPrompt(tool.prompt);
  };

  const pollJob = async (jobId) => {
    for (let i = 0; i < 120; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const { data } = await axios.get(`${API}/apps/jobs/${jobId}`);
        if (data.status === 'completed') return data.result;
        if (data.status === 'failed') throw new Error(data.error || 'Edit failed');
      } catch (err) {
        if (err.message !== 'Edit failed') continue;
        throw err;
      }
    }
    throw new Error('Timed out');
  };

  const handleApply = async () => {
    if (!editFile || !selectedTool) return;
    setEditing(true);
    setEditResult(null);
    try {
      const formData = new FormData();
      formData.append('app_id', selectedTool.id);
      formData.append('prompt', editPrompt);
      formData.append('image', editFile);
      const { data } = await axios.post(`${API}/apps/run`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.info(`Applying ${selectedTool.label}...`);
      const result = await pollJob(data.job_id);
      setEditResult(result.image_url);
      toast.success(`${selectedTool.label} applied!`);
    } catch (err) {
      toast.error(err.message || 'Edit failed');
    } finally {
      setEditing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="edit-panel">
      <input type="file" ref={editFileRef} accept="image/*" onChange={handleEditUpload} className="hidden" />

      {!editPreview ? (
        <div className="flex flex-col items-center py-16">
          <button onClick={() => editFileRef.current?.click()} data-testid="edit-upload-btn"
            className="w-full max-w-lg h-48 rounded-2xl border-2 border-dashed border-zinc-700 bg-zinc-900/30 hover:border-orange-500/40 hover:bg-zinc-900/50 transition-all flex flex-col items-center justify-center gap-3">
            <Upload className="w-10 h-10 text-zinc-500" />
            <span className="text-sm text-zinc-400">Upload an image to edit</span>
            <span className="text-[10px] text-zinc-600">PNG, JPEG, WebP</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
          {/* Image Preview */}
          <div>
            <div className="rounded-xl overflow-hidden border border-zinc-800 bg-black relative">
              <div className="grid grid-cols-2 gap-[1px] bg-zinc-800">
                <div className="bg-black p-2 relative">
                  <p className="absolute top-3 left-3 text-[9px] text-zinc-500 uppercase tracking-wider font-medium z-10">Original</p>
                  <img src={editPreview} alt="Original" className="w-full aspect-square object-contain" />
                </div>
                <div className="bg-black p-2 relative">
                  <p className="absolute top-3 left-3 text-[9px] text-zinc-500 uppercase tracking-wider font-medium z-10">Result</p>
                  {editResult ? (
                    <img src={editResult} alt="Result" className="w-full aspect-square object-contain" data-testid="edit-result-image" />
                  ) : editing ? (
                    <div className="w-full aspect-square flex items-center justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-orange-500/50" />
                    </div>
                  ) : (
                    <div className="w-full aspect-square flex items-center justify-center text-zinc-700 text-xs">
                      Select a tool to start
                    </div>
                  )}
                </div>
              </div>
            </div>
            {editResult && (
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={() => {
                  const link = document.createElement('a');
                  link.href = editResult;
                  link.download = `edited_${selectedTool?.id}.png`;
                  link.target = '_blank';
                  link.click();
                }} className="flex-1 gap-1.5">
                  <Download className="w-3.5 h-3.5" /> Download
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditResult(null); }} className="gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> Reset
                </Button>
              </div>
            )}
          </div>

          {/* Tool Panel */}
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-2 block">Editing Tools</Label>
              <div className="grid grid-cols-2 gap-2">
                {EDIT_TOOLS.map(tool => (
                  <button key={tool.id} data-testid={`edit-tool-${tool.id}`}
                    onClick={() => selectTool(tool)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      selectedTool?.id === tool.id
                        ? 'bg-orange-500/10 border-orange-500/30 text-white'
                        : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                    }`}>
                    <tool.icon className="w-4 h-4 mb-1.5" />
                    <p className="text-xs font-medium">{tool.label}</p>
                    <p className="text-[9px] opacity-50">{tool.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {selectedTool && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-zinc-500 mb-1 block">Instructions</Label>
                  <Textarea data-testid="edit-prompt-input"
                    value={editPrompt} onChange={e => setEditPrompt(e.target.value)}
                    placeholder="Describe what you want..."
                    className="bg-zinc-900/80 border-zinc-800 rounded-xl min-h-[70px] text-sm" />
                </div>
                <Button data-testid="edit-apply-btn" onClick={handleApply} disabled={editing}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 gap-2">
                  {editing ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying...</> : <><Wand2 className="w-4 h-4" /> Apply {selectedTool.label}</>}
                </Button>
              </div>
            )}

            <button onClick={() => { setEditFile(null); setEditPreview(null); setEditResult(null); setSelectedTool(null); }}
              className="w-full text-center text-xs text-zinc-600 hover:text-zinc-400 transition-colors py-2">
              Upload a different image
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const ImagesStudioPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [activeTab, setActiveTab] = useState('create');
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [style, setStyle] = useState('photorealistic');
  const [selectedModel, setSelectedModel] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [latestResult, setLatestResult] = useState(null);

  // Fetch generated images
  const fetchGeneratedImages = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/images/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedImages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch images:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchGeneratedImages();
  }, [fetchGeneratedImages]);

  // Cycle through generation messages
  useEffect(() => {
    if (generating) {
      const interval = setInterval(() => {
        setCurrentMessage(prev => (prev + 1) % GENERATION_MESSAGES.length);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [generating]);

  // Simulate progress
  useEffect(() => {
    if (generating) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [generating]);

  // Handle inspiration click
  const handleInspirationClick = (example) => {
    setPrompt(example.prompt);
    toast.success('Prompt loaded! Customize it or generate now.');
  };

  // Generate image
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe what you want to create');
      return;
    }

    setGenerating(true);
    setProgress(0);
    setLatestResult(null);

    try {
      const response = await axios.post(
        `${API}/images/generate`,
        {
          prompt: prompt.trim(),
          negative_prompt: negativePrompt.trim(),
          style: style,
          provider: selectedModel || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProgress(100);
      setLatestResult(response.data);
      toast.success('Image created successfully!');
      
      // Refresh gallery
      setTimeout(() => {
        fetchGeneratedImages();
        setActiveTab('gallery');
      }, 1000);

    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setGenerating(false);
    }
  };

  // Handle upload
  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.info('Uploading image...');
      await axios.post(`${API}/uploads/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Image uploaded!');
      fetchGeneratedImages();
      setActiveTab('gallery');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10 pb-20">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute top-20 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Images Engine</p>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight text-white">
              AI Image Studio
            </h1>
            <p className="text-zinc-500 text-base max-w-2xl">
              Describe your vision and watch it come to life.
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-8">
              <TabsList className="bg-zinc-900/80 border border-zinc-800 p-1 rounded-full">
                <TabsTrigger 
                  value="create" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create
                </TabsTrigger>
                <TabsTrigger 
                  value="edit" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Edit
                </TabsTrigger>
                <TabsTrigger 
                  value="upload" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </TabsTrigger>
                <TabsTrigger 
                  value="gallery" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                >
                  <Layers className="w-4 h-4 mr-2" />
                  Gallery
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Create Tab - Prompt First Experience */}
            <TabsContent value="create" className="space-y-8 animate-fade-in-up">
              {/* Main Prompt Area */}
              <div className="max-w-3xl mx-auto">
                {/* Prompt Input */}
                <div className="relative mb-6">
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-purple-500/10 to-primary/20 rounded-3xl blur-xl opacity-60" />
                  <div className="relative">
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Describe what you want to create..."
                      className="prompt-input w-full px-6 py-5 text-lg"
                      disabled={generating}
                      rows={4}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          handleGenerate();
                        }
                      }}
                    />
                    <div className="absolute bottom-4 right-4 text-xs text-zinc-500 hidden sm:block">
                      ⌘/Ctrl + Enter to generate
                    </div>
                  </div>
                </div>

                {/* Model Selector */}
                <div className="mb-6">
                  <Label className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-2 block">Model</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: null, label: 'Auto', desc: 'Best available', color: 'from-zinc-500/20 to-zinc-600/20', border: 'border-zinc-500/20' },
                      { value: 'flux', label: 'Flux Kontext', desc: 'Fastest', color: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-500/20' },
                      { value: 'nanobanana', label: 'Nano Banana', desc: 'Gemini Pro', color: 'from-yellow-500/20 to-orange-500/20', border: 'border-yellow-500/20' },
                      { value: 'openai', label: 'GPT Image', desc: 'OpenAI', color: 'from-green-500/20 to-emerald-500/20', border: 'border-green-500/20' },
                    ].map(m => (
                      <button key={String(m.value)}
                        onClick={() => setSelectedModel(m.value)}
                        data-testid={`model-${m.value || 'auto'}`}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          selectedModel === m.value
                            ? `bg-gradient-to-br ${m.color} ${m.border} ring-1 ring-white/10`
                            : 'border-zinc-700/50 bg-zinc-800/30 hover:border-zinc-600/50'
                        }`}>
                        <p className="text-xs font-semibold text-zinc-200">{m.label}</p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">{m.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <div className="flex flex-col items-center gap-4 mb-8">
                  <Button
                    onClick={handleGenerate}
                    disabled={!prompt.trim() || generating}
                    className={`btn-generate min-w-[200px] ${generating ? 'btn-generating' : ''}`}
                    data-testid="generate-image-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {GENERATION_MESSAGES[currentMessage]}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate Image
                      </>
                    )}
                  </Button>

                  {/* Progress Bar */}
                  {generating && (
                    <div className="w-full max-w-md space-y-2 animate-fade-in-up">
                      <div className="progress-creative">
                        <div 
                          className="progress-creative-bar" 
                          style={{ width: `${Math.max(10, progress)}%` }}
                        />
                      </div>
                      <p className="text-center text-sm text-zinc-500">
                        {progress > 0 ? `${Math.round(progress)}% complete` : 'Starting...'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Advanced Settings (Collapsed by Default) */}
                <div className="collapsible-panel mb-8">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="collapsible-trigger"
                  >
                    <span className="flex items-center gap-2">
                      <Wand2 className="w-4 h-4" />
                      Customize Style & Settings
                    </span>
                    {showAdvanced ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  
                  {showAdvanced && (
                    <div className="collapsible-content animate-fade-in-up space-y-4">
                      {/* Style Selection */}
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Style</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {STYLE_OPTIONS.map((s) => (
                            <button
                              key={s.value}
                              onClick={() => setStyle(s.value)}
                              className={`p-3 rounded-xl border text-left transition-all ${
                                style === s.value
                                  ? 'bg-primary/10 border-primary text-white'
                                  : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                              }`}
                            >
                              <p className="font-medium text-sm">{s.label}</p>
                              <p className="text-xs opacity-60">{s.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Negative Prompt */}
                      <div className="space-y-2">
                        <Label className="text-zinc-400 flex items-center gap-2">
                          <span>Negative Prompt</span>
                          <span className="text-xs text-zinc-600">(what to avoid)</span>
                        </Label>
                        <Textarea
                          value={negativePrompt}
                          onChange={(e) => setNegativePrompt(e.target.value)}
                          placeholder="blurry, low quality, distorted, watermark..."
                          className="bg-zinc-900/80 border-zinc-800 rounded-xl min-h-[80px] text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Inspiration Section */}
              {!generating && (
                <div className="space-y-6">
                  <div className="section-divider">
                    <span className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Get Inspired
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {INSPIRATION_EXAMPLES.map((example, index) => {
                      const Icon = example.icon;
                      return (
                        <button
                          key={index}
                          onClick={() => handleInspirationClick(example)}
                          className="group relative bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4 text-left hover:border-zinc-700 hover:bg-zinc-800/60 transition-all hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${example.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-1">
                            {example.category}
                          </p>
                          <p className="text-sm text-zinc-300 line-clamp-2 group-hover:text-white transition-colors">
                            {example.prompt.slice(0, 60)}...
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Latest Result */}
              {latestResult && !generating && (
                <div className="max-w-2xl mx-auto animate-fade-in-up">
                  <div className="result-card">
                    <div className="aspect-square relative">
                      <img
                        src={latestResult.image_url}
                        alt={latestResult.prompt}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="status-badge status-ready">
                          <Check className="w-3 h-3" />
                          Created
                        </span>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <p className="text-sm text-zinc-400 line-clamp-2">{latestResult.prompt}</p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => setActiveTab('gallery')}
                          className="flex-1"
                        >
                          <Layers className="w-4 h-4 mr-2" />
                          View in Gallery
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setPrompt(latestResult.prompt);
                            setLatestResult(null);
                          }}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Remix
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Edit Tab */}
            <TabsContent value="edit" className="animate-fade-in-up">
              <EditPanel token={token} onComplete={() => { fetchGeneratedImages(); setActiveTab('gallery'); }} />
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="animate-fade-in-up">
              <div className="max-w-2xl mx-auto">
                <div className="border-2 border-dashed border-zinc-700 rounded-3xl p-12 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="cursor-pointer block">
                    <div className="w-20 h-20 mx-auto mb-6 bg-zinc-800 rounded-2xl flex items-center justify-center">
                      <Upload className="w-10 h-10 text-zinc-500" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Upload Your Images</h3>
                    <p className="text-zinc-500 mb-6">
                      Drag and drop or click to browse
                    </p>
                    <p className="text-xs text-zinc-600">
                      Supports JPEG, PNG, WebP, GIF • Max 50MB
                    </p>
                  </label>
                </div>

                {/* Upload Use Cases */}
                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
                    <ZoomIn className="w-6 h-6 mx-auto mb-2 text-primary" />
                    <p className="text-sm font-medium">Upscale & Enhance</p>
                    <p className="text-xs text-zinc-500 mt-1">Improve quality with AI</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
                    <Palette className="w-6 h-6 mx-auto mb-2 text-purple-400" />
                    <p className="text-sm font-medium">Remove Background</p>
                    <p className="text-xs text-zinc-500 mt-1">Isolate subjects instantly</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
                    <ImageIcon className="w-6 h-6 mx-auto mb-2 text-blue-400" />
                    <p className="text-sm font-medium">Convert to Video</p>
                    <p className="text-xs text-zinc-500 mt-1">Animate your images</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery" className="animate-fade-in-up">
              <MediaGallery
                items={generatedImages.map(img => ({
                  ...img,
                  type: 'image',
                  source: img.source || 'generated'
                }))}
                token={token}
                onRefresh={fetchGeneratedImages}
                contentType="image"
                showFilters={true}
                emptyMessage="No images yet"
                emptyAction={
                  <Button onClick={() => setActiveTab('create')} className="btn-oracle">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Your First Image
                  </Button>
                }
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
    </AppShell>
  );
};

export default ImagesStudioPage;
